import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  MapPin,
  User,
  Calendar as CalendarIcon,
} from 'lucide-react';
import {
  format,
  startOfWeek,
  addDays,
  addWeeks,
  subWeeks,
  isSameDay,
  parseISO,
  setHours,
  setMinutes,
  isBefore,
  isAfter,
  addMinutes,
} from 'date-fns';
import { is } from 'date-fns/locale';
import { useAuthStore } from '@/stores/auth';
import { viewings, agents, settings, type Viewing, type BusinessHours } from '@/lib/api';
import { formatTime, cn } from '@/lib/utils';
import { useHolidays, getHoliday, formatDateKey } from '@/lib/holidays';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Day key mapping for business hours
const DAY_KEYS: (keyof BusinessHours)[] = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

interface TimeSlot {
  time: string; // HH:mm format
  hour: number;
  minute: number;
  isAvailable: boolean;
  isWorkingHours: boolean;
  viewing?: Viewing;
}

interface AgentCalendarProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialAgentId?: string;
}

export function AgentCalendar({
  open,
  onOpenChange,
  initialAgentId,
}: AgentCalendarProps) {
  const { token } = useAuthStore();
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [selectedAgentId, setSelectedAgentId] = useState<string | undefined>(
    initialAgentId
  );

  const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 }); // Monday start
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  // Fetch agents list
  const { data: agentsData } = useQuery({
    queryKey: ['agents'],
    queryFn: () => agents.list(token!),
    enabled: !!token && open,
  });

  // Fetch agency settings (for business hours)
  const { data: agencySettings } = useQuery({
    queryKey: ['settings'],
    queryFn: () => settings.get(token!),
    enabled: !!token && open,
  });

  // Fetch holidays
  const { data: holidayList = [] } = useHolidays(weekStart.getFullYear());

  // Fetch viewings for selected agent in current week
  const { data: viewingsData, isLoading: isLoadingViewings } = useQuery({
    queryKey: [
      'agent-viewings',
      selectedAgentId,
      format(weekStart, 'yyyy-MM-dd'),
    ],
    queryFn: () =>
      viewings.list(token!, {
        from: format(weekStart, 'yyyy-MM-dd'),
        to: format(addDays(weekStart, 7), 'yyyy-MM-dd'),
      }),
    enabled: !!token && open && !!selectedAgentId,
  });

  // Filter viewings for selected agent
  const agentViewings = useMemo(() => {
    if (!viewingsData?.data || !selectedAgentId) return [];
    return viewingsData.data.filter(
      (v) => v.agent_id === selectedAgentId && v.status !== 'aflyst'
    );
  }, [viewingsData?.data, selectedAgentId]);

  // Get active agents for dropdown
  const activeAgents = useMemo(() => {
    return agentsData?.data?.filter((a) => a.active) || [];
  }, [agentsData?.data]);

  // Auto-select first agent if none selected
  useMemo(() => {
    if (!selectedAgentId && activeAgents.length > 0) {
      setSelectedAgentId(activeAgents[0].id);
    }
  }, [activeAgents, selectedAgentId]);

  // Generate time slots for a day (08:00 - 20:00, 30-minute intervals)
  const generateTimeSlots = (
    day: Date,
    dayViewings: Viewing[],
    businessHours?: BusinessHours
  ): TimeSlot[] => {
    const slots: TimeSlot[] = [];
    const dayOfWeek = day.getDay();
    const dayKey = DAY_KEYS[dayOfWeek];
    const dayHours = businessHours?.[dayKey];

    // Default working hours if not specified
    const workStart = dayHours?.open
      ? parseInt(dayHours.open.split(':')[0])
      : 9;
    const workEnd = dayHours?.close
      ? parseInt(dayHours.close.split(':')[0])
      : 17;

    // Generate slots from 08:00 to 20:00
    for (let hour = 8; hour < 20; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeStr = `${hour.toString().padStart(2, '0')}:${minute
          .toString()
          .padStart(2, '0')}`;
        const slotStart = setMinutes(setHours(day, hour), minute);
        const slotEnd = addMinutes(slotStart, 30);

        // Check if this slot overlaps with any viewing
        const overlappingViewing = dayViewings.find((viewing) => {
          const viewingStart = parseISO(viewing.scheduled_at);
          const viewingEnd = addMinutes(viewingStart, viewing.duration_minutes || 60);

          // Check for overlap
          return (
            (isBefore(slotStart, viewingEnd) || slotStart.getTime() === viewingEnd.getTime()) &&
            (isAfter(slotEnd, viewingStart) || slotEnd.getTime() === viewingStart.getTime()) &&
            !(slotEnd.getTime() === viewingStart.getTime()) &&
            !(slotStart.getTime() === viewingEnd.getTime())
          );
        });

        const isWorkingHours = hour >= workStart && hour < workEnd;
        const isAvailable = isWorkingHours && !overlappingViewing;

        slots.push({
          time: timeStr,
          hour,
          minute,
          isAvailable,
          isWorkingHours,
          viewing: overlappingViewing,
        });
      }
    }

    return slots;
  };

  // Get viewings for a specific day
  const getViewingsForDay = (day: Date): Viewing[] => {
    return agentViewings.filter((v) => isSameDay(parseISO(v.scheduled_at), day));
  };

  // Get selected agent name
  const selectedAgent = activeAgents.find((a) => a.id === selectedAgentId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-prima-600" />
            Dagatal söluaðila
          </DialogTitle>
          <DialogDescription>
            Skoðaðu tímabókanir og lausan tíma fyrir valinn söluaðila
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Agent selector and week navigation */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            {/* Agent selector */}
            <div className="flex items-center gap-3">
              <User className="w-5 h-5 text-gray-500" />
              <Select
                value={selectedAgentId}
                onValueChange={setSelectedAgentId}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Veldu söluaðila" />
                </SelectTrigger>
                <SelectContent>
                  {activeAgents.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Week navigation */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentWeek(subWeeks(currentWeek, 1))}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Fyrri vika"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div className="text-center min-w-[200px]">
                <span className="font-medium text-gray-900">
                  {format(weekStart, 'd. MMM', { locale: is })} -{' '}
                  {format(addDays(weekStart, 6), 'd. MMM yyyy', { locale: is })}
                </span>
              </div>
              <button
                onClick={() => setCurrentWeek(addWeeks(currentWeek, 1))}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Næsta vika"
              >
                <ChevronRight className="w-5 h-5 text-gray-600" />
              </button>
              <button
                onClick={() => setCurrentWeek(new Date())}
                className="ml-2 px-3 py-1.5 text-sm text-prima-600 hover:bg-prima-50 rounded-lg transition-colors"
              >
                Í dag
              </button>
            </div>
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-green-100 border border-green-300"></div>
              <span className="text-gray-600">Laust</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-red-100 border border-red-300"></div>
              <span className="text-gray-600">Bókað</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-gray-100 border border-gray-300"></div>
              <span className="text-gray-600">Utan vinnutíma</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-orange-100 border border-orange-300"></div>
              <span className="text-gray-600">Frídagur</span>
            </div>
          </div>

          {/* Calendar grid */}
          {isLoadingViewings ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-prima-600" />
            </div>
          ) : !selectedAgentId ? (
            <div className="flex items-center justify-center h-64">
              <p className="text-gray-500">Veldu söluaðila til að sjá dagatal</p>
            </div>
          ) : (
            <div className="overflow-auto max-h-[calc(90vh-280px)]">
              {/* Desktop view - full calendar grid */}
              <div className="hidden md:block">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="w-16 p-2 text-xs text-gray-500 font-medium text-left border-b">
                        Tími
                      </th>
                      {weekDays.map((day, idx) => {
                        const isToday = isSameDay(day, new Date());
                        const dayOfWeek = day.getDay();
                        const icelandicIdx =
                          dayOfWeek === 0 ? 6 : dayOfWeek - 1;
                        const dayName = [
                          'Mán',
                          'Þri',
                          'Mið',
                          'Fim',
                          'Fös',
                          'Lau',
                          'Sun',
                        ][icelandicIdx];
                        const holiday = getHoliday(formatDateKey(day), holidayList);

                        return (
                          <th
                            key={idx}
                            className={cn(
                              'p-2 text-center border-b min-w-[100px]',
                              isToday && 'bg-prima-50',
                              holiday?.type === 'closed' && 'bg-orange-50',
                              holiday?.type === 'half_day' && 'bg-amber-50'
                            )}
                          >
                            <p className="text-xs text-gray-500 font-medium uppercase">
                              {dayName}
                            </p>
                            <p
                              className={cn(
                                'text-lg font-bold',
                                isToday ? 'text-prima-600' : 'text-gray-900',
                                holiday?.type === 'closed' && 'text-orange-700'
                              )}
                            >
                              {format(day, 'd')}
                            </p>
                            {holiday && (
                              <p className={cn(
                                'text-[10px] truncate',
                                holiday.type === 'closed' ? 'text-orange-600 font-medium' :
                                holiday.type === 'half_day' ? 'text-amber-600 font-medium' :
                                'text-gray-400'
                              )}>
                                {holiday.name}
                              </p>
                            )}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Generate time rows (08:00 - 19:30, every 30 minutes) */}
                    {Array.from({ length: 24 }, (_, i) => {
                      const hour = Math.floor(i / 2) + 8;
                      const minute = (i % 2) * 30;
                      const timeStr = `${hour
                        .toString()
                        .padStart(2, '0')}:${minute
                        .toString()
                        .padStart(2, '0')}`;

                      return (
                        <tr key={timeStr} className="border-b border-gray-100">
                          <td className="p-1 text-xs text-gray-500 font-medium border-r">
                            {timeStr}
                          </td>
                          {weekDays.map((day, dayIdx) => {
                            const dayViewings = getViewingsForDay(day);
                            const holiday = getHoliday(formatDateKey(day), holidayList);
                            const isDayClosed = holiday?.type === 'closed';
                            const isHalfDay = holiday?.type === 'half_day';
                            const halfDayCloseHour = isHalfDay && holiday?.close_at
                              ? parseInt(holiday.close_at.split(':')[0])
                              : undefined;
                            // For half-day holidays, slots after close_at are closed
                            const isHolidayClosed = isDayClosed || (isHalfDay && halfDayCloseHour !== undefined && hour >= halfDayCloseHour);

                            const slots = generateTimeSlots(
                              day,
                              dayViewings,
                              agencySettings?.business_hours
                            );
                            const slot = slots.find(
                              (s) => s.hour === hour && s.minute === minute
                            );
                            const isToday = isSameDay(day, new Date());

                            if (!slot) return <td key={dayIdx}></td>;

                            // Check if this is the start of a viewing
                            const viewingStartsHere =
                              slot.viewing &&
                              format(
                                parseISO(slot.viewing.scheduled_at),
                                'HH:mm'
                              ) === timeStr;

                            // Calculate viewing span
                            const viewingSpan = viewingStartsHere
                              ? Math.ceil(
                                  (slot.viewing?.duration_minutes || 60) / 30
                                )
                              : 0;

                            // Skip cells that are part of a viewing (not the start)
                            if (slot.viewing && !viewingStartsHere) {
                              return null; // Will be covered by rowSpan
                            }

                            return (
                              <td
                                key={dayIdx}
                                rowSpan={viewingSpan || 1}
                                className={cn(
                                  'border-r border-gray-100 p-0.5 align-top',
                                  isToday && 'bg-prima-50/30',
                                  isHolidayClosed && 'bg-orange-50/60',
                                  !isHolidayClosed && !slot.isWorkingHours && 'bg-gray-50',
                                  !isHolidayClosed && slot.isAvailable &&
                                    slot.isWorkingHours &&
                                    'bg-green-50 hover:bg-green-100 cursor-pointer transition-colors',
                                  slot.viewing && 'bg-red-50'
                                )}
                                title={
                                  isHolidayClosed
                                    ? `${holiday?.name} — Lokað`
                                    : slot.viewing
                                    ? `${slot.viewing.property_address} - ${slot.viewing.lead_name}`
                                    : slot.isAvailable
                                    ? 'Laust'
                                    : 'Utan vinnutíma'
                                }
                              >
                                {viewingStartsHere && slot.viewing && (
                                  <div className="bg-red-100 border border-red-300 rounded p-1 text-xs h-full min-h-[40px]">
                                    <div className="flex items-center gap-1 text-red-800 font-medium truncate">
                                      <Clock className="w-3 h-3 flex-shrink-0" />
                                      {formatTime(slot.viewing.scheduled_at)}
                                    </div>
                                    <div className="text-red-700 truncate mt-0.5">
                                      {slot.viewing.property_address}
                                    </div>
                                    {slot.viewing.lead_name && (
                                      <div className="text-red-600 truncate text-[10px]">
                                        {slot.viewing.lead_name}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile view - day cards */}
              <div className="md:hidden space-y-4">
                {weekDays.map((day, idx) => {
                  const dayViewings = getViewingsForDay(day);
                  const isToday = isSameDay(day, new Date());
                  const dayOfWeek = day.getDay();
                  const icelandicIdx = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
                  const dayName = [
                    'Mánudagur',
                    'Þriðjudagur',
                    'Miðvikudagur',
                    'Fimmtudagur',
                    'Föstudagur',
                    'Laugardagur',
                    'Sunnudagur',
                  ][icelandicIdx];
                  const holiday = getHoliday(formatDateKey(day), holidayList);

                  return (
                    <div
                      key={idx}
                      className={cn(
                        'border rounded-lg overflow-hidden',
                        isToday && 'ring-2 ring-prima-500',
                        holiday?.type === 'closed' && 'border-orange-300'
                      )}
                    >
                      <div
                        className={cn(
                          'px-4 py-2 font-medium',
                          holiday?.type === 'closed'
                            ? 'bg-orange-100 text-orange-800'
                            : holiday?.type === 'half_day'
                            ? 'bg-amber-50 text-amber-800'
                            : isToday
                            ? 'bg-prima-100 text-prima-800'
                            : 'bg-gray-100 text-gray-800'
                        )}
                      >
                        <div className="flex items-center justify-between">
                          <span>{dayName} {format(day, 'd. MMM', { locale: is })}</span>
                          {holiday && (
                            <span className={cn(
                              'text-xs px-2 py-0.5 rounded-full',
                              holiday.type === 'closed' ? 'bg-orange-200 text-orange-800' :
                              holiday.type === 'half_day' ? 'bg-amber-200 text-amber-800' :
                              'bg-gray-200 text-gray-600'
                            )}>
                              {holiday.name}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="p-3">
                        {holiday?.type === 'closed' ? (
                          <p className="text-sm text-orange-600 text-center py-4 font-medium">
                            Lokað — {holiday.name}
                          </p>
                        ) : dayViewings.length === 0 ? (
                          <p className="text-sm text-gray-500 text-center py-4">
                            Engar skoðanir
                          </p>
                        ) : (
                          <div className="space-y-2">
                            {dayViewings.map((viewing) => (
                              <div
                                key={viewing.id}
                                className="bg-red-50 border border-red-200 rounded-lg p-3"
                              >
                                <div className="flex items-center gap-2 text-sm font-medium text-red-800">
                                  <Clock className="w-4 h-4" />
                                  {formatTime(viewing.scheduled_at)}
                                  <span className="text-red-600">
                                    ({viewing.duration_minutes || 60} min)
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-red-700 mt-1">
                                  <MapPin className="w-4 h-4" />
                                  {viewing.property_address}
                                </div>
                                {viewing.lead_name && (
                                  <div className="flex items-center gap-2 text-sm text-red-600 mt-1">
                                    <User className="w-4 h-4" />
                                    {viewing.lead_name}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Summary stats */}
          {selectedAgentId && !isLoadingViewings && (
            <div className="flex items-center justify-between pt-4 border-t">
              <div className="text-sm text-gray-600">
                <span className="font-medium">{selectedAgent?.name}</span>
                {' - '}
                {agentViewings.length} skoðanir þessa viku
              </div>
              <div className="flex items-center gap-4 text-sm">
                <span className="text-green-600">
                  {weekDays.reduce((count, day) => {
                    const dayViewings = getViewingsForDay(day);
                    const slots = generateTimeSlots(
                      day,
                      dayViewings,
                      agencySettings?.business_hours
                    );
                    return (
                      count + slots.filter((s) => s.isAvailable).length
                    );
                  }, 0)}{' '}
                  lausir tímar
                </span>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default AgentCalendar;
